import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiGallection, PapiPremiumFolderData } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/24x24/EyeDisc2';
import Premium from '@wix/da-ds/pkg/Icons/24x24/Premium';
import Button from '@wix/da-shared-react/pkg/Button';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import type { BIEventData } from '@wix/da-bi/pkg/types';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import FolderPaywallLayout from './FolderPaywallLayout';

import s from './FolderPaywall.scss';

export interface Props {
  folder: PapiGallection;
  premiumFolderData: PapiPremiumFolderData;
  biData?: BIEventData | BiEvent;
  isCurrentUserProfileOwner: boolean;
}

const FolderPaywall: React.FC<Props> = ({
  folder,
  biData,
  premiumFolderData,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const { formattedDollarPrice, isPaidFolder, isWatchToAccess, subproductId } =
    normalizePremiumFolderData(folder.premiumFolderData);
  const contentOwner = folder.owner;

  if (isPaidFolder) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<Premium size={IconSize.MEDIUM} />}
        button={
          <Button
            view="b18"
            fullWidth={isMobile}
            href={subproductId ? Url.checkoutLink(subproductId) : undefined}
            biData={biData}
          >
            <span className={s['buttonLabel']}>
              {t('paywall.premium.folder.unlockFor')} {formattedDollarPrice}
            </span>
          </Button>
        }
      />
    );
  }

  if (isWatchToAccess) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<EyeDisc2 size={IconSize.MEDIUM} />}
        button={
          <WatchButton
            view="b1"
            user={contentOwner!}
            fullWidth={isMobile}
            onToggleWatchSuccess={() => {
              window.location.href = premiumFolderData.galleryUrl!;
            }}
          />
        }
      />
    );
  }

  return null;
};

FolderPaywall.displayName = 'FolderPaywall';

export default FolderPaywall;
